/* eslint-disable prettier/prettier */
import React, {useRef, useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {Box, Divider, Fade, Flex, Text, useMultiStyleConfig} from '@chakra-ui/react'
import ProductTileBundle from './partials/product-tile-bundle'
import AddToCartButton from '../add-to-cart-button'
import CurrentPrice from '../display-price/current-price'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import {FormattedMessage, useIntl} from 'react-intl'
import {API_ERROR_MESSAGE} from '../../constants'
import {remCalc} from '../../theme/_utils'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
import {getPriceDataUseProduct} from '../../utils/product-utils'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {chunk} from 'lodash'
import {BRAND_KEY} from '../../brand'

const Bundle = ({title, bundleDetails}) => {
    const styles = useMultiStyleConfig('Bundle')
    const {currency} = useCurrency()
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')

    const [showErrorMessage, setShowErrorMessage] = useState(0)
    const errorContainerRef = useRef(null)
    const {formatMessage} = useIntl()

    {
        /* Calculate the total price of the bundle */
    }
    const bundleTotalPrice =
        bundleDetails?.find((child) => child.totalBundlePrice)?.totalBundlePrice || 0

    {
        /* Retrieve the bundle id from one of the products */
    }
    const productSetId = bundleDetails.find((item) => item.productSetID)?.productSetID

    const bundlesData = bundleDetails.filter((item) => !item.totalBundlePrice)

    const {data: bundleProductsData, isLoading} = useProducts({
        parameters: {
            ids: bundlesData.slice(1).map((p) => p.productId), // remove first element, the PDP product
            allImages: false,
        },
    })

    const bundleCount = bundleDetails?.length - 1 || 0
    const lgWidthBundle =
    BRAND_KEY === 'kenwood'
        ? `calc(5.2% + (23.7% * ${bundleCount}))`
        : `calc(25% * ${bundleCount})`

    const {addLayer} = useGoogleTagManager()
    useEffect(() => {
        if (bundleProductsData && !isLoading) {
            addLayer(polish_ecommerce_gtm_event.bind(this))
            chunk(bundleProductsData.data || [], 10).forEach((bundleChunk, index) => {
                addLayer(
                    item_gtm_event.bind(this, {
                        event: 'view_item_list',
                        bundle_id: productSetId,
                        bundle: 'true',
                        item_list_name: 'bundle',
                        items: bundleChunk?.data?.map((product, i) => ({
                            item_name: product?.c_defaultName,
                            item_id: product?.c_variants?.[0]?.productId,
                            item_category: product?.c_defaultCategoryName,
                            item_variant: product?.id || product?.c_subModelIdentifier,
                            price: + (product?.price / (1 + product?.c_taxRate / 100)).toFixed(2),
                            fullprice: getPriceDataUseProduct(product)?.isOnSale
                                ? 'reduced'
                                : 'fullprice',
                            rating_points: '',
                            erp_category: product?.c_materialGroup || product?.c_MaterialGroup,
                            prdha_category: product?.c_prdhaCategory || product?.c_prdha_category,
                            index: i + 1 + (index * 10),
                            stock_status:
                                product?.inventory?.stockLevel > 0 ? 'in-stock' : 'out-of-stock',
                        })),
                    }),
                )
            })
        }
    }, [bundleProductsData, isLoading])

    return (
        <Flex {...styles.outerContainer} data-testid="bundle-product-view">
            <Text {...styles.headerLabel} data-testid="bundle-product-title">
                {title}
            </Text>
            <Flex {...styles.innerContainer}>
                <Flex sx={styles.productsContainer} data-testid="bundle-product-container">
                    {bundlesData.map((bundleData, i) => {
                        return (
                            <ProductTileBundle
                                key={i}
                                product={bundleData}
                                firstElement={i === 0}
                            />
                        )
                    })}
                </Flex>
            </Flex>
            <Divider {...styles.divider} />
            <Flex
                {...styles.addCartButtonContainer}
                sx={{
                    width: {
                        base: '100%',
                        lg: lgWidthBundle,
                    },
                }}
            >
                <Flex {...styles.subtotalPrice}>
                    <FormattedMessage defaultMessage="Subtotal:" id="bundle_subtotal.text" />
                    <CurrentPrice
                        price={bundleTotalPrice}
                        currency={currency}
                        sx={{
                            whiteSpace: 'nowrap',
                            textStyle: 'head-small',
                            marginLeft: {base: 0, lg: remCalc(16)},
                            marginRight: {base: 0, lg: remCalc(16)},
                        }}
                    />
                </Flex>
                {!notShoppableCountry && (
                    <Flex {...styles.addCartButton} data-testid="bundle-product-add-to-cart-button">
                        <AddToCartButton
                            product={bundlesData}
                            setShowErrorMessage={setShowErrorMessage}
                            errorContainerRef={errorContainerRef}
                            showUniversalFeedback={true}
                            bundleID={productSetId}
                        />
                    </Flex>
                )}
            </Flex>
            <Box ref={errorContainerRef}>
                {showErrorMessage ? (
                    <Fade in={true}>
                        <Text color="text.danger" fontWeight="semibold" marginBottom={8}>
                            {formatMessage(API_ERROR_MESSAGE)}
                        </Text>
                    </Fade>
                ) : (
                    <></>
                )}
            </Box>
        </Flex>
    )
}

Bundle.propTypes = {
    bundle: PropTypes.object,
    bundleDetails: PropTypes.array,
    title: PropTypes.node,
}

export default Bundle
